import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import {
  T, serif, fmt, Card, Chip, Boton, Campo, estiloInput,
  Titulo, Aviso, Vacio, Cargando,
} from '../lib/ui';
import { mensajeDeError } from './Gastos';

// ════════════════════════════════════════════════════════════════════
// Revisión contable
//
// Es una vista distinta de la de aprobar, y la diferencia importa:
//
//   El jefe decide si el gasto PROCEDE.
//   Contabilidad decide si el papel SUSTENTA el asiento.
//
// Por eso aquí se ve lo que allá no: la foto del sustento, el estado
// del proveedor en SUNAT, y el rastro completo de quién capturó, quién
// corrigió y quién aprobó. Incluye los ya aprobados: contabilidad
// puede observar algo que el jefe dejó pasar.
// ════════════════════════════════════════════════════════════════════

const FILTROS = [
  ['aprobado', 'Por contabilizar'],
  ['enviado', 'En aprobación'],
  ['observado', 'Observados'],
  ['contabilizado', 'En SAP'],
  ['', 'Todos'],
];

export function Revision({ usuario }) {
  const [filtro, setFiltro] = useState('aprobado');
  const [lista, setLista] = useState(null);
  const [abierto, setAbierto] = useState(null);
  const [aviso, setAviso] = useState(null);

  const cargar = () => {
    setLista(null);
    api.gastos(filtro ? { estado: filtro } : {})
      .then((r) => setLista(r.gastos || []))
      .catch((err) => { setAviso({ tipo: 'bad', texto: mensajeDeError(err) }); setLista([]); });
  };

  useEffect(cargar, [filtro]);   // eslint-disable-line

  return (
    <>
      <Titulo sub="Revisa el sustento antes de contabilizar">Revisión contable</Titulo>

      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}

      <div style={{ display: 'flex', gap: 7, marginBottom: 14, flexWrap: 'wrap' }}>
        {FILTROS.map(([id, txt]) => (
          <button key={id || 'todos'} onClick={() => setFiltro(id)} style={{
            padding: '7px 14px', borderRadius: 16, fontSize: 13, cursor: 'pointer',
            border: `1px solid ${filtro === id ? T.navy : T.border}`,
            background: filtro === id ? T.navy : T.paper,
            color: filtro === id ? '#fff' : T.text,
          }}>
            {txt}
          </button>
        ))}
      </div>

      {!lista ? <Cargando /> : lista.length === 0 ? (
        <Vacio>No hay gastos en este estado.</Vacio>
      ) : (
        <div style={{ display: 'grid', gap: 9 }}>
          {lista.map((g) => (
            <Card key={g.id} onClick={() => setAbierto(g.id)} style={{ padding: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 11 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip estado={g.estado} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.navy }}>
                      {g.autorNombre || 'Sin identificar'}
                    </span>
                    {g.consumidor && (
                      <span style={{ fontSize: 11, color: T.muted }}>
                        · lo gastó {g.consumidor}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginTop: 5 }}>
                    {g.concepto || g.proveedorRazon || 'Sin concepto'}
                  </div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                    {fmt.fecha(g.fecha)}
                    {g.comprobanteSerie ? ` · ${g.comprobanteSerie}-${g.comprobanteCorrelativo}` : ''}
                    {g.ovId ? ` · ${g.ovId}` : ''}
                    {` · ${g.cuentaPcge || 'sin cuenta'}`}
                  </div>

                  {g.proveedorCondicion === 'no_habido' && (
                    <div style={{ fontSize: 11, color: T.bad, marginTop: 4, fontWeight: 600 }}>
                      Proveedor NO HABIDO
                    </div>
                  )}
                </div>
                <div style={{
                  fontFamily: serif, fontSize: 17, fontWeight: 700,
                  color: T.navy, whiteSpace: 'nowrap',
                }}>
                  {fmt.money(g.total, g.moneda)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {abierto && (
        <FichaRevision
          id={abierto} usuario={usuario}
          onCerrar={() => setAbierto(null)}
          onHecho={(texto) => {
            setAbierto(null);
            if (texto) setAviso({ tipo: 'ok', texto });
            cargar();
          }}
        />
      )}
    </>
  );
}

// ── La ficha completa ───────────────────────────────────────────────
function FichaRevision({ id, usuario, onCerrar, onHecho }) {
  const [d, setD] = useState(null);
  const [observando, setObservando] = useState(false);
  const [nota, setNota] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [ampliada, setAmpliada] = useState(null);
  const [puedeForzar, setPuedeForzar] = useState(false);
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [discrepanciaTc, setDiscrepanciaTc] = useState(null);
  const [avisoTc, setAvisoTc] = useState('');

  useEffect(() => {
    api.revisionGasto(id).then(setD).catch(() => setD({}));
  }, [id]);

  if (!d) return null;
  const g = d.gasto;
  if (!g) return null;

  const contabilizar = async (forzar = false) => {
    setOcupado(true);
    setError('');
    try {
      const r = await api.contabilizarGasto(id, forzar);
      // La política puede dejar pasar un tipo de cambio que no coincide
      // con el de SAP. Si pasó, se dice: el asiento queda con una
      // diferencia que alguien tendrá que justificar.
      if (r?.aviso) setAvisoTc(r.aviso);
      onHecho(r.detalle || 'Contabilizado.');
    } catch (err) {
      const d = err.detalle;
      setError(d?.detalle || 'No se pudo contabilizar.');
      // Si el único reparo es que no hay foto, se puede insistir
      setPuedeForzar(Boolean(d?.puedeForzar));
      // Discrepancia con la tabla de SAP: se muestran las dos cifras,
      // porque "no se pudo contabilizar" no le sirve a nadie.
      setDiscrepanciaTc(d?.error === 'tipo_cambio_discrepa' ? d.tipoCambio : null);
      setOcupado(false);
    }
  };

  const observar = async () => {
    if (!nota.trim()) return;
    setOcupado(true);
    try {
      await api.decidirGasto(id, 'observar', nota.trim());
      onHecho('Gasto observado. Vuelve a quien lo rindió para que lo corrija.');
    } catch (err) {
      setError(err.detalle?.detalle || 'No se pudo observar.');
      setOcupado(false);
    }
  };

  const COLOR = { ok: T.ok, aviso: T.warn, malo: T.bad, sin_verificar: T.muted };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 95, background: 'rgba(10,15,26,.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={onCerrar}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: T.cream, borderRadius: '14px 14px 0 0',
        width: '100%', maxWidth: 620, maxHeight: '93vh', overflowY: 'auto',
        padding: '18px 16px 26px',
      }}>
        {/* Cabecera */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: 12, marginBottom: 14,
        }}>
          <div style={{ minWidth: 0 }}>
            <Chip estado={g.estado} />
            <div style={{ fontSize: 17, fontWeight: 600, marginTop: 6 }}>
              {g.concepto || 'Sin concepto'}
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>
              {g.id} · {fmt.fecha(g.fecha)}
            </div>
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <div style={{
              fontFamily: serif, fontSize: 23, fontWeight: 700, color: T.navy,
            }}>
              {fmt.money(g.total, g.moneda)}
            </div>
            {/* En moneda extranjera, la cifra que se asienta es la de
                soles. Mostrar solo el importe original obligaba a
                contabilidad a convertirlo por su cuenta. */}
            {g.moneda && g.moneda !== 'PEN' && (
              <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                {g.totalSoles
                  ? `= ${fmt.money(g.totalSoles, 'PEN')}`
                  : 'sin tipo de cambio'}
                {g.tipoCambio ? ` · TC ${Number(g.tipoCambio).toFixed(4)}` : ''}
              </div>
            )}
          </div>
        </div>

        {error && <Aviso tipo="bad">{error}</Aviso>}

        {/* Un gasto en moneda extranjera sin tipo de cambio no se puede
            asentar: el libro de compras va en soles. */}
        {g.moneda && g.moneda !== 'PEN' && !g.tipoCambio && (
          <Aviso tipo="warn">
            Este gasto está en {g.moneda} y no tiene tipo de cambio guardado.
            Hay que ponerlo antes de contabilizarlo: el libro de compras y el
            crédito fiscal van en soles, al tipo de cambio venta de la fecha
            de emisión.
          </Aviso>
        )}

        {/* A quién se le imputa el consumo. Es el dato que falta para
            saber en qué se fue el dinero de un encargo cuando quien lo
            administra no es quien lo gasta. */}
        {d.consumidor && (
          <Card style={{ marginBottom: 12, padding: 13 }}>
            <div style={{
              fontSize: 10, color: T.muted, fontWeight: 700,
              letterSpacing: '.05em', marginBottom: 6,
            }}>
              QUIÉN CONSUMIÓ EL GASTO
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>
              {d.consumidor.nombre}
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
              {d.consumidor.cargo ? `${d.consumidor.cargo} · ` : ''}
              {d.consumidor.enPadron
                ? 'del padrón de personal'
                : 'escrito a mano, no está en el padrón'}
              {' · rendido por '}{d.autor?.nombre || 'sin identificar'}
            </div>
          </Card>
        )}

        {/* Quién lo rindió */}
        <Card style={{ marginBottom: 12, padding: 13 }}>
          <div style={{
            fontSize: 10, color: T.muted, fontWeight: 700,
            letterSpacing: '.05em', marginBottom: 8,
          }}>
            QUIÉN LO RINDIÓ
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.navy }}>
            {d.autor?.nombre || 'Sin identificar'}
          </div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
            {d.autor?.centro} · {d.autor?.rol}
            {d.aprobador && ` · aprobado por ${d.aprobador}`}
          </div>
        </Card>

        {/* El sustento: lo que contabilidad viene a ver */}
        <Card style={{ marginBottom: 12, padding: 13 }}>
          <div style={{
            fontSize: 10, color: T.muted, fontWeight: 700,
            letterSpacing: '.05em', marginBottom: 10,
          }}>
            EL SUSTENTO
          </div>

          {d.adjuntos?.length > 0 ? (
            <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
              {d.adjuntos.map((a) => (
                <Sustento key={a.id} gastoId={g.id} adjunto={a} onAmpliar={setAmpliada} />
              ))}
            </div>
          ) : (
            <Aviso tipo="warn">
              Sin foto adjunta. Si el sustento es un comprobante físico,
              obsérvalo para que suban la foto antes de contabilizar.
            </Aviso>
          )}
        </Card>

        {/* Datos del comprobante */}
        <Card style={{ marginBottom: 12, padding: 13 }}>
          {[
            ['Tipo', g.tipoDocumento?.replace(/_/g, ' ')],
            ['Comprobante', g.comprobanteSerie
              ? `${g.comprobanteSerie}-${g.comprobanteCorrelativo}`
              : g.numeroDocumento],
            ['Proveedor', g.proveedorRazon],
            ['RUC', g.proveedorRuc],
            ['Cuenta', g.cuentaPcge ? `${g.cuentaPcge} · ${d.cuentaNombre || ''}` : null],
            ['Orden de venta', g.ovId ? `${g.ovId}${d.ov ? ` · ${d.ov.clienteAlias}` : ''}` : 'Sin OV'],
            ['Motivo sin OV', !g.ovId ? g.motivoSinOv?.replace(/_/g, ' ') : null],
            ['Subtotal', g.subtotal ? fmt.money(g.subtotal, g.moneda) : null],
            ['IGV', g.igv ? fmt.money(g.igv, g.moneda) : null],
            ['Medio de pago', g.medioPago],
            ['Centro', g.centro],
          ].filter(([, v]) => v).map(([k, v]) => (
            <div key={k} style={{
              display: 'flex', justifyContent: 'space-between',
              gap: 12, padding: '4px 0', fontSize: 13,
            }}>
              <span style={{ color: T.muted }}>{k}</span>
              <span style={{ textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </Card>

        {/* Trayectos de movilidad */}
        {d.trayectos?.length > 0 && (
          <Card style={{ marginBottom: 12, padding: 13 }}>
            <div style={{
              fontSize: 10, color: T.muted, fontWeight: 700,
              letterSpacing: '.05em', marginBottom: 8,
            }}>
              DESPLAZAMIENTOS
            </div>
            {d.trayectos.map((t) => (
              <div key={t.id} style={{
                display: 'flex', justifyContent: 'space-between',
                gap: 10, padding: '4px 0', fontSize: 12,
                borderBottom: `1px solid ${T.border}`,
              }}>
                <span style={{ minWidth: 0 }}>
                  {fmt.fecha(t.fecha)} · {t.origen} → {t.destino}
                  <span style={{ display: 'block', color: T.muted, fontSize: 11 }}>
                    {t.motivo}
                  </span>
                </span>
                <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {Number(t.importe).toFixed(2)}
                </span>
              </div>
            ))}
          </Card>
        )}

        {/* Estado del proveedor en SUNAT */}
        {d.evaluacionProveedor && (
          <Card style={{ marginBottom: 12, padding: 13 }}>
            <div style={{
              fontSize: 10, color: T.muted, fontWeight: 700,
              letterSpacing: '.05em', marginBottom: 8,
            }}>
              EL PROVEEDOR EN SUNAT
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{
                width: 10, height: 10, borderRadius: 5, flexShrink: 0,
                background: COLOR[d.evaluacionProveedor.nivel] || T.muted,
              }} />
              <span style={{
                fontSize: 14, fontWeight: 600,
                color: COLOR[d.evaluacionProveedor.nivel] || T.navy,
              }}>
                {d.evaluacionProveedor.texto}
              </span>
            </div>
            {d.evaluacionProveedor.detalle && (
              <div style={{ fontSize: 11.5, color: T.muted, marginTop: 6, lineHeight: 1.5 }}>
                {d.evaluacionProveedor.detalle}
              </div>
            )}
          </Card>
        )}

        {/* El rastro */}
        {d.historial?.length > 0 && (
          <Card style={{ marginBottom: 14, padding: 13 }}>
            <div style={{
              fontSize: 10, color: T.muted, fontWeight: 700,
              letterSpacing: '.05em', marginBottom: 8,
            }}>
              QUÉ LE HA PASADO A ESTE GASTO
            </div>
            {d.historial.map((h, i) => (
              <div key={i} style={{
                display: 'flex', gap: 10, padding: '4px 0', fontSize: 11.5,
              }}>
                <span style={{ color: T.muted, whiteSpace: 'nowrap', minWidth: 96 }}>
                  {fmt.fechaHora(h.createdAt)}
                </span>
                <span>
                  <strong>{h.accion.replace(/_/g, ' ')}</strong>
                  {h.quien ? ` · ${h.quien}` : ''}
                </span>
              </div>
            ))}
          </Card>
        )}

        {g.observacion && (
          <Aviso tipo="warn">Observación actual: {g.observacion}</Aviso>
        )}

        {/* Acciones */}
        {observando ? (
          <>
            <Campo etiqueta="Qué hay que corregir"
                   ayuda="Lo lee quien rindió el gasto. Sé concreto.">
              <textarea value={nota} rows={3}
                        onChange={(e) => setNota(e.target.value)}
                        placeholder="Por ejemplo: falta la foto de la factura"
                        style={{ ...estiloInput, resize: 'vertical' }} />
            </Campo>
            <div style={{ display: 'flex', gap: 8 }}>
              <Boton variante="suave" onClick={() => setObservando(false)}
                     disabled={ocupado} style={{ flex: 1 }}>
                Cancelar
              </Boton>
              <Boton variante="acento" onClick={observar}
                     disabled={ocupado || !nota.trim()} style={{ flex: 1.3 }}>
                {ocupado ? 'Un momento…' : 'Observar'}
              </Boton>
            </div>
          </>
        ) : (
          <>
            {/* ── Discrepancia con la tabla de SAP ──────────────────
                No se fuerza con un botón: hay que decidir cuál es el
                tipo de cambio de la fecha de emisión, y eso se
                corrige, no se salta. */}
            {discrepanciaTc && (
              <div style={{
                border: `1px solid ${T.bad}`, borderRadius: 8,
                padding: 12, marginBottom: 12,
              }}>
                <div style={{
                  fontSize: 12.5, fontWeight: 600, color: T.bad, marginBottom: 8,
                }}>
                  El tipo de cambio no coincide con el de SAP
                </div>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr',
                  gap: 8, fontSize: 12.5, marginBottom: 8,
                }}>
                  <div style={{ background: T.okBg, borderRadius: 6, padding: '8px 10px' }}>
                    <div style={{ fontSize: 10, color: T.muted, fontWeight: 700 }}>
                      RINDE · fecha de emisión
                    </div>
                    <div style={{ fontWeight: 700, color: T.navy }}>
                      {Number(discrepanciaTc.rinde).toFixed(4)}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      {fmt.money(Number(g.total) * Number(discrepanciaTc.rinde), 'PEN')}
                    </div>
                  </div>
                  <div style={{ background: T.warnBg, borderRadius: 6, padding: '8px 10px' }}>
                    <div style={{ fontSize: 10, color: T.muted, fontWeight: 700 }}>
                      Tabla de SAP
                    </div>
                    <div style={{ fontWeight: 700, color: T.navy }}>
                      {discrepanciaTc.sap != null
                        ? Number(discrepanciaTc.sap).toFixed(4)
                        : 'sin cargar'}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      {discrepanciaTc.sap != null
                        ? fmt.money(Number(g.total) * Number(discrepanciaTc.sap), 'PEN')
                        : '—'}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.45 }}>
                  El del documento define el crédito fiscal, y debe ser el venta
                  de la SUNAT al {fmt.fecha(discrepanciaTc.fecha)}. Si el de RINDE
                  está mal, corrígelo aquí; si el de SAP está mal, hay que
                  arreglar su tabla.
                </div>
              </div>
            )}

            {avisoTc && <Aviso tipo="warn">{avisoTc}</Aviso>}

            {puedeForzar && (
              <div style={{ marginBottom: 10 }}>
                <Boton variante="peligro" onClick={() => contabilizar(true)}
                       disabled={ocupado} style={{ width: '100%' }}>
                  Contabilizar igual, sin foto de sustento
                </Boton>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 5 }}>
                  Queda registrado que se aceptó sin sustento visual.
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {d.puede?.contabilizar && (
                <Boton variante="exito" onClick={() => contabilizar(false)}
                       disabled={ocupado} style={{ flex: 1.4 }}>
                  {ocupado ? 'Un momento…' : 'Contabilizar y enviar a SAP'}
                </Boton>
              )}
              {d.puede?.corregir && (
                <Boton variante="suave" onClick={() => setCorrigiendo(true)} style={{ flex: 1 }}>
                  Corregir
                </Boton>
              )}
              {d.puede?.observar && (
                <Boton variante="suave" onClick={() => setObservando(true)} style={{ flex: 1 }}>
                  Observar
                </Boton>
              )}
              <Boton variante="suave" onClick={onCerrar} style={{ flex: 1 }}>
                Cerrar
              </Boton>
            </div>
          </>
        )}

        {g.estado === 'aprobado' && (
          <div style={{ fontSize: 11.5, color: T.muted, marginTop: 12, lineHeight: 1.5 }}>
            Este gasto ya fue aprobado. Si el sustento no alcanza, obsérvalo:
            vuelve a quien lo rindió con tu comentario, aunque el jefe ya lo
            hubiera dado por bueno.
          </div>
        )}

        {corrigiendo && (
          <CorregirContable
            gasto={g} cuentaNombre={d.cuentaNombre}
            onCerrar={() => setCorrigiendo(false)}
            onHecho={() => { setCorrigiendo(false); onHecho('Gasto corregido.'); }}
          />
        )}

        {/* Foto ampliada */}
        {ampliada && (
          <div
            onClick={() => setAmpliada(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 150, background: 'rgba(10,15,26,.92)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
              cursor: 'zoom-out',
            }}>
            <img src={ampliada} alt="Sustento"
                 style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </div>
        )}
      </div>
    </div>
  );
}


// ── Una foto de sustento ────────────────────────────────────────────
//
// Se trae con la sesión y se convierte en una dirección temporal. Una
// etiqueta <img src> normal no envía el token: el servidor devolvía 401
// y la foto salía rota, justo lo que contabilidad necesita ver.
function Sustento({ gastoId, adjunto, onAmpliar }) {
  const [url, setUrl] = useState(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    if (adjunto.enBase === false) return undefined;
    let vivo = true;
    let creada = null;
    api.adjunto(gastoId, adjunto.id)
      .then((u) => { if (vivo) { creada = u; setUrl(u); } else URL.revokeObjectURL(u); })
      .catch(() => { if (vivo) setFallo(true); });
    return () => {
      vivo = false;
      // Liberar la memoria del navegador al cerrar la ficha
      if (creada) URL.revokeObjectURL(creada);
    };
  }, [gastoId, adjunto.id, adjunto.enBase]);

  const esImagen = (adjunto.mimeType || '').startsWith('image/');
  // Si el servidor ya sabe que el archivo no está, no vale la pena
  // intentar traerlo: se dice directamente.
  const perdido = adjunto.enBase === false;
  const marco = {
    width: 120, height: 120, borderRadius: 8,
    border: `1px solid ${T.border}`, background: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, color: T.muted, textAlign: 'center', padding: 6,
  };

  return (
    <div style={{ width: 120 }}>
      {perdido || fallo ? (
        <div style={{ ...marco, color: T.bad, flexDirection: 'column', gap: 4 }}>
          <span style={{ fontWeight: 600 }}>Sustento perdido</span>
          <span style={{ fontSize: 10, color: T.muted, lineHeight: 1.3 }}>
            Se fue en un despliegue anterior. Pídelo de nuevo.
          </span>
        </div>
      ) : !url ? (
        /* Puede estar en disco en lugar de en la base: se intenta igual,
           porque antes se daba por perdido sin preguntar. */
        <div style={marco}>Cargando…</div>
      ) : esImagen ? (
        <img
          src={url} alt={adjunto.nombre || 'Sustento'}
          onClick={() => onAmpliar(url)}
          style={{
            ...marco, objectFit: 'cover', cursor: 'zoom-in',
            display: 'block', padding: 0,
          }}
        />
      ) : (
        <a href={url} target="_blank" rel="noreferrer"
           style={{ ...marco, color: T.navy, textDecoration: 'none' }}>
          Abrir {adjunto.mimeType?.includes('pdf') ? 'PDF' : 'archivo'}
        </a>
      )}
      <div style={{ fontSize: 10, color: T.muted, marginTop: 4 }}>
        {adjunto.tipo?.replace(/_/g, ' ')} · {Math.round((adjunto.bytes || 0) / 1024)} KB
      </div>
    </div>
  );
}


// ── Corrección contable ─────────────────────────────────────────────
//
// Contabilidad arregla lo que no cuadra en vez de devolverlo: la cuenta
// mal elegida, el desglose que no suma, la razón social incompleta.
//
// El TOTAL no se toca: esa cifra la decidió el aprobador. Si está mal,
// hay que observar el gasto, no corregirlo por detrás.
function CorregirContable({ gasto, cuentaNombre, onCerrar, onHecho }) {
  const [cuentas, setCuentas] = useState([]);
  const [f, setF] = useState({
    cuentaPcge: gasto.cuentaPcge || '',
    concepto: gasto.concepto || '',
    proveedorRazon: gasto.proveedorRazon || '',
    subtotal: gasto.subtotal != null ? String(gasto.subtotal) : '',
    igv: gasto.igv != null ? String(gasto.igv) : '',
    exonerado: gasto.exonerado != null && Number(gasto.exonerado) > 0
      ? String(gasto.exonerado) : '',
    cuentaExonerado: gasto.cuentaExonerado || '639306',
    moneda: gasto.moneda || 'PEN',
    tipoCambio: gasto.tipoCambio != null ? String(gasto.tipoCambio) : '',
    consumidorNombre: gasto.consumidorNombre || '',
  });
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  useEffect(() => {
    api.catalogos()
      .then((c) => setCuentas(c.cuentas || []))
      .catch(() => setCuentas([]));
  }, []);

  const total = Number(gasto.total) || 0;
  const sub = Number(f.subtotal) || 0;
  const igv = Number(f.igv) || 0;
  const exo = Number(f.exonerado) || 0;
  const suma = sub + igv + exo;
  const cuadra = !f.subtotal || Math.abs(suma - total) <= 0.05;

  // Señal de que la factura trae algo sin IGV: si la relación entre el
  // IGV y la base no es 18%, una de las dos cifras no corresponde a un
  // consumo íntegramente gravado.
  const tasa = sub > 0 ? (igv / sub) * 100 : null;
  const sospecha = exo === 0 && tasa != null && Math.abs(tasa - 18) > 0.6;

  // Despeja el desglose a partir del IGV, que es la cifra que el
  // proveedor no puede inventar: base = IGV / 0.18, y lo que sobra del
  // total es lo exonerado.
  const derivar = () => {
    if (igv <= 0 || total <= 0) return;
    const gravado = igv / 0.18;
    const resto = total - gravado - igv;
    if (resto < -0.05) {
      setError('Con ese IGV la base ya supera el total. Revisa el papel.');
      return;
    }
    setError('');
    setF({
      ...f,
      subtotal: gravado.toFixed(2),
      exonerado: Math.max(0, resto).toFixed(2),
    });
  };

  const guardar = async () => {
    setOcupado(true);
    setError('');
    try {
      const cambios = {
        cuentaPcge: f.cuentaPcge,
        concepto: f.concepto,
        proveedorRazon: f.proveedorRazon || null,
      };
      if (f.subtotal !== '') cambios.subtotal = Number(f.subtotal);
      if (f.igv !== '') cambios.igv = Number(f.igv);
      if (f.exonerado !== '') {
        cambios.exonerado = Number(f.exonerado);
        cambios.cuentaExonerado = exo > 0 ? (f.cuentaExonerado || '639306') : null;
      }
      if (f.consumidorNombre !== (gasto.consumidorNombre || '')) {
        cambios.consumidorNombre = f.consumidorNombre || null;
        // Si se reescribe el nombre a mano, el vínculo con el padrón ya
        // no se puede dar por bueno: lo decide quien corrige.
        cambios.consumidorId = null;
      }
      if (f.moneda !== (gasto.moneda || 'PEN')) cambios.moneda = f.moneda;
      if (f.moneda !== 'PEN' && parseFloat(f.tipoCambio) > 0) {
        cambios.tipoCambio = parseFloat(f.tipoCambio);
        cambios.tipoCambioFuente = 'Corregido por contabilidad';
      }
      const r = await api.editarGasto(gasto.id, cambios);
      // El servidor avisa si una columna pendiente impidió guardar una
      // parte. Antes esto pasaba en silencio.
      if (r?.aviso) { setAviso(r.aviso); setOcupado(false); return; }
      onHecho();
    } catch (err) {
      setError(err.detalle?.detalle || 'No se pudo guardar.');
      setOcupado(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 130, background: 'rgba(10,15,26,.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      overflowY: 'auto',
    }} onClick={onCerrar}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: 20,
        width: '100%', maxWidth: 440, maxHeight: '92vh', overflowY: 'auto',
      }}>
        <div style={{
          fontFamily: serif, fontSize: 19, fontWeight: 600,
          color: T.navy, marginBottom: 4,
        }}>
          Corregir para contabilizar
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 16 }}>
          {gasto.id} · queda registrado que lo corrigió contabilidad
        </div>

        {error && <Aviso tipo="bad">{error}</Aviso>}
        {aviso && <Aviso tipo="warn">{aviso}</Aviso>}

        <Campo etiqueta="Cuenta contable">
          <select value={f.cuentaPcge}
                  onChange={(e) => setF({ ...f, cuentaPcge: e.target.value })}
                  style={estiloInput}>
            {cuentas.length === 0 && (
              <option value={f.cuentaPcge}>{f.cuentaPcge} · {cuentaNombre}</option>
            )}
            {cuentas.map((c) => (
              <option key={c.valor} value={c.valor}>{c.valor} · {c.texto}</option>
            ))}
          </select>
        </Campo>

        <Campo etiqueta="Concepto">
          <input value={f.concepto} onChange={(e) => setF({ ...f, concepto: e.target.value })}
                 style={estiloInput} />
        </Campo>

        <Campo etiqueta="Proveedor">
          <input value={f.proveedorRazon}
                 onChange={(e) => setF({ ...f, proveedorRazon: e.target.value })}
                 style={estiloInput} />
        </Campo>

        {/* Quien consumió el gasto. Se escribe a mano al capturar, así
            que es el campo que más se va a equivocar: contabilidad tiene
            que poder arreglarlo sin devolver el gasto. */}
        <Campo
          etiqueta="Quién consumió el gasto"
          ayuda="Vacío = lo consumió quien lo rindió."
        >
          <input value={f.consumidorNombre}
                 onChange={(e) => setF({ ...f, consumidorNombre: e.target.value })}
                 placeholder="Apellidos y nombres"
                 style={estiloInput} />
        </Campo>

        {/* ── Moneda y tipo de cambio ─────────────────────────────────
            Un gasto capturado en soles cuando la factura decía dólares
            queda subvaluado casi cuatro veces. Contabilidad lo corrige
            aquí sin tener que devolver el gasto. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', columnGap: 10 }}>
          <Campo etiqueta="Moneda">
            <select value={f.moneda}
                    onChange={(e) => setF({
                      ...f,
                      moneda: e.target.value,
                      tipoCambio: e.target.value === 'PEN' ? '' : f.tipoCambio,
                    })}
                    style={estiloInput}>
              {['PEN', 'USD', 'EUR', 'MXN', 'COP', 'CLP', 'BRL', 'ARS'].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Campo>
          <Campo etiqueta={f.moneda === 'PEN' ? 'Tipo de cambio' : `1 ${f.moneda} = S/`}>
            <input inputMode="decimal" value={f.tipoCambio}
                   placeholder="0.0000"
                   disabled={f.moneda === 'PEN'}
                   onChange={(e) => setF({ ...f, tipoCambio: e.target.value })}
                   style={{ ...estiloInput, opacity: f.moneda === 'PEN' ? 0.5 : 1 }} />
          </Campo>
        </div>
        {f.moneda !== 'PEN' && (
          <div style={{ fontSize: 11, color: T.muted, marginTop: -8, marginBottom: 14, lineHeight: 1.4 }}>
            El venta de la SUNAT a la fecha de emisión ({fmt.fecha(gasto.fecha)}),
            que es el que exige la norma para el crédito fiscal.
            {parseFloat(f.tipoCambio) > 0 && total > 0 && (
              <>
                {' '}Asienta{' '}
                <strong style={{ color: T.navy }}>
                  {fmt.money(total * parseFloat(f.tipoCambio), 'PEN')}
                </strong>.
              </>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 10 }}>
          <Campo etiqueta="Base gravada">
            <input inputMode="decimal" value={f.subtotal}
                   onChange={(e) => setF({ ...f, subtotal: e.target.value })}
                   style={estiloInput} />
          </Campo>
          <Campo etiqueta="IGV">
            <input inputMode="decimal" value={f.igv}
                   onChange={(e) => setF({ ...f, igv: e.target.value })}
                   style={estiloInput} />
          </Campo>
        </div>

        {/* ── La parte exonerada ──────────────────────────────────────
            El recargo al consumo no paga IGV pero sí se paga: forma
            parte del total de la factura y va a su propia cuenta. Si
            aquí hay un importe, al SAP viajan DOS líneas. */}
        <div style={{
          border: `1px solid ${exo > 0 ? T.navy : T.border}`,
          borderRadius: 8, padding: 12, marginBottom: 14,
        }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: T.navy, marginBottom: 2 }}>
            Parte exonerada de IGV
          </div>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 10, lineHeight: 1.4 }}>
            Recargo al consumo y servicios inafectos. Si pones un importe,
            al SAP viajan dos líneas en lugar de una.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', columnGap: 10 }}>
            <Campo etiqueta="Importe">
              <input inputMode="decimal" value={f.exonerado}
                     placeholder="0.00"
                     onChange={(e) => setF({ ...f, exonerado: e.target.value })}
                     style={estiloInput} />
            </Campo>
            <Campo etiqueta="Cuenta">
              <input value={f.cuentaExonerado}
                     onChange={(e) => setF({ ...f, cuentaExonerado: e.target.value })}
                     disabled={exo <= 0}
                     style={{ ...estiloInput, opacity: exo > 0 ? 1 : 0.5 }} />
            </Campo>
          </div>
          {igv > 0 && total > 0 && (
            <Boton variante="suave" onClick={derivar}
                   style={{ width: '100%', fontSize: 12.5 }}>
              Despejar desde el IGV ({(igv / 0.18).toFixed(2)} gravado)
            </Boton>
          )}
        </div>

        {sospecha && (
          <div style={{
            background: T.badBg, borderRadius: 8, padding: '10px 12px',
            marginBottom: 14, fontSize: 12.5, color: T.bad,
          }}>
            El IGV es el {tasa.toFixed(1)}% de la base, no el 18%.
            <div style={{ fontSize: 11, color: T.muted, marginTop: 4, lineHeight: 1.4 }}>
              O la factura trae una parte exonerada que todavía no está
              separada aquí, o una de las dos cifras no es la del papel.
              Compáralo con el comprobante antes de guardar.
            </div>
          </div>
        )}

        <div style={{
          background: cuadra ? T.okBg : T.badBg, borderRadius: 8,
          padding: '10px 12px', marginBottom: 14, fontSize: 12.5,
          color: cuadra ? T.ok : T.bad,
        }}>
          {cuadra
            ? `${sub.toFixed(2)} + ${igv.toFixed(2)} IGV`
              + (exo > 0 ? ` + ${exo.toFixed(2)} exonerado` : '')
              + ` = ${fmt.money(total, gasto.moneda)}. Cuadra.`
            : `${suma.toFixed(2)} no da el total de ${total.toFixed(2)}.`}
          <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
            El total no se puede cambiar aquí: lo decidió el aprobador.
            Si está mal, observa el gasto.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Boton variante="suave" onClick={onCerrar} disabled={ocupado} style={{ flex: 1 }}>
            Cancelar
          </Boton>
          <Boton variante="acento" onClick={guardar}
                 disabled={ocupado || !cuadra} style={{ flex: 1.3 }}>
            {ocupado ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
      </div>
    </div>
  );
}
